import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import { useClient } from '../../contexts/ClientContext';
import AuthLayout from '../layout/AuthLayout';
import toast from 'react-hot-toast';
import { Eye, EyeOff, User, Mail, Lock, Loader2, CheckCircle, Gift } from 'lucide-react';
import { motion } from 'framer-motion';
import { GoogleOAuthButton } from './GoogleOAuthButton';

interface RegisterData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
  referral_code?: string;
}

const RegisterForm: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuth();
  const { clearClientState } = useClient();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = useState(false); // State for password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false); // State for confirm password visibility
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [selectedTier, setSelectedTier] = useState<string | null>(null);
  const [billingCycle, setBillingCycle] = useState<string>('monthly');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting, isValid },
  } = useForm<RegisterData>({ mode: 'onChange' });

  // Check for referral code and tier selection in URL parameters
  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode);
      setValue('referral_code', refCode);
      // Note: Only the referrer gets credits, not the new user
    }
    
    // Check for tier selection (for paid tier signup)
    const tier = searchParams.get('tier');
    const billing = searchParams.get('billing');
    if (tier) {
      setSelectedTier(tier);
    }
    if (billing) {
      setBillingCycle(billing);
    }
  }, [searchParams, setValue]);

  const password = watch('password');
  const agreeToTerms = watch('agreeToTerms');

  const onSubmit = async (data: RegisterData) => {
    try {
      // Track signup started
      const { trackEvent } = await import('../../lib/analytics');
      const { trackSignup } = await import('../../lib/metaPixel');
      trackEvent('signup_started');
      
      // Clear any existing client state before registration
      clearClientState();
      const { confirmPassword, agreeToTerms, ...registerData } = data;
      
      // Get UTM data from localStorage
      const utmData = localStorage.getItem('utm_data');
      const parsedUtmData = utmData ? JSON.parse(utmData) : {};
      
      // Get referrer from document
      const referrerUrl = document.referrer || undefined;
      
      // Merge registration data with UTM tracking
      const registrationPayload = {
        ...registerData,
        ...parsedUtmData,
        referrer_url: referrerUrl,
      };
      
      await registerUser(registrationPayload);
      
      // Track signup completed
      trackEvent('signup_completed', { plan: selectedTier || 'free' });
      trackSignup('email'); // Track Meta Pixel signup conversion
      const { trackConversion } = await import('../../lib/analytics');
      trackConversion('signup', 0);
      
      // If user selected a paid tier, redirect to login with tier info
      if (selectedTier && selectedTier !== 'free') {
        toast.success('Registration successful! Please login to complete your subscription.');
        // Redirect to login with tier info so we can redirect to checkout after login
        navigate(`/login?tier=${selectedTier}&billing=${billingCycle}`);
      } else {
        // Show success message and redirect to login for free tier
        toast.success('Registration successful! Please check your email to verify your account before logging in.');
        navigate('/login');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    }
  };



  return (
    <AuthLayout
      title="Create your account"
      subtitle="Join thousands of creators and businesses"
    >
      <motion.form 
        onSubmit={handleSubmit(onSubmit)} 
        className="space-y-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        {/* Username Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('username', {
                required: 'Username is required',
                minLength: {
                  value: 3,
                  message: 'Username must be at least 3 characters',
                },
              })}
              type="text"
              placeholder="Username"
              className="w-full pl-12 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
          </div>
          {errors.username && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.username.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Email Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              type="email"
              placeholder="Email address"
              className="w-full pl-12 pr-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
          </div>
          {errors.email && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.email.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Referral Code Display (if present) */}
        {referralCode && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3, delay: 0.35 }}
          >
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Gift className="h-5 w-5 text-blue-400" />
              </div>
              <input
                {...register('referral_code')}
                type="text"
                value={referralCode}
                readOnly
                placeholder="Referral code"
                className="w-full pl-12 pr-4 py-3 bg-blue-700/20 border border-blue-500/50 rounded-xl text-blue-300 placeholder-blue-400 backdrop-blur-sm cursor-not-allowed"
              />
              <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                <span className="text-xs text-blue-400 font-medium">Referred by friend</span>
              </div>
            </div>
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-blue-400 flex items-center space-x-1"
            >
              <Gift className="h-4 w-4" />
              <span>You're joining through a friend's referral link</span>
            </motion.p>
          </motion.div>
        )}

        {/* Password Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              })}
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              className="w-full pl-12 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-300 focus:outline-none transition-colors"
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.password && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.password.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Confirm Password Field */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: value =>
                  value === password || 'Passwords do not match',
              })}
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm password"
              className="w-full pl-12 pr-12 py-3 bg-slate-700/50 border border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-slate-100 placeholder-slate-400 backdrop-blur-sm"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-300 focus:outline-none transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>{errors.confirmPassword.message}</span>
            </motion.p>
          )}
        </motion.div>

        {/* Terms and Conditions Checkbox */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <div className="flex items-center space-x-2">
            <input
              {...register('agreeToTerms', { required: true })}
              type="checkbox"
              id="agreeToTerms"
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="agreeToTerms" className="text-sm text-slate-400">
              I agree to the{' '}
              <Link to="/terms" className="underline hover:text-blue-400">
                Terms and Conditions
              </Link>
              {' '}
              and{' '}
              <Link to="/privacy" className="underline hover:text-blue-400">
                Privacy Policy
              </Link>
              .
            </label>
          </div>
          {errors.agreeToTerms && (
            <motion.p 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-1 text-sm text-red-500 flex items-center space-x-1"
            >
              <span>⚠️</span>
              <span>You must agree to the terms and conditions.</span>
            </motion.p>
          )}
        </motion.div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting || !isValid}
          className="w-full text-white py-3 px-6 rounded-xl font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: 'linear-gradient(to right, #30b3dd, #4a298a)', focusRingColor: '#30b3dd' }}
          onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #2a9cc7, #3f2479)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'linear-gradient(to right, #30b3dd, #4a298a)'}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.7 }}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Creating account...</span>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <CheckCircle className="w-5 h-5" />
              <span>Create Account</span>
            </div>
          )}
        </motion.button>
      </motion.form>

      {/* Divider */}
      <motion.div 
        className="mt-4 mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.8 }}
      >
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-600"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-slate-900 text-slate-400">or continue with</span>
          </div>
        </div>
      </motion.div>

      {/* Google OAuth Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.9 }}
      >
        <GoogleOAuthButton 
          disabled={isSubmitting}
          tierId={selectedTier || undefined}
          billingCycle={billingCycle}
        />
      </motion.div>

      <motion.div 
        className="mt-4 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 1.0 }}
      >
        <p className="text-slate-400">
          Already have an account?{' '}
          <Link
            to="/login"
            className="text-blue-400 hover:text-blue-300 font-semibold transition-colors"
          >
            Sign in
          </Link>
        </p>
      </motion.div>
    </AuthLayout>
  );
};

export default RegisterForm;